// What both the server and the screens need about Cases: limits, the shapes
// the REST answers have, and the rules of the chain. No dependencies, so the
// screen (ui-core) imports it as `@trameria/core/cases-shared` and draws
// optimistic changes with the same rules the server applies.
//
// Promise:
// - The current steps are the open steps whose parents are all closed. A
//   parent missing from the list counts as closed (lists only carry open steps).
// - The ball is decided from the current steps only: an Action or a waiting
//   whose kind is the owner's own turn → me; else a current waiting that is
//   overdue or whose target was dropped → due; else waitings only → them;
//   no current step → none.
// - A step added without parents follows the step added before it in the
//   same call, or the only leaf; with no steps it is a root; with two or more
//   leaves it is not decided (null).

export const CASE_STATUSES = ["open", "done", "dropped"] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];
export const CASE_STEP_KINDS = ["action", "waiting"] as const;
export type CaseStepKind = (typeof CASE_STEP_KINDS)[number];
export type CaseBall = "me" | "due" | "them" | "none";
export const CASE_LINK_SLOTS = ["body", "result"] as const;
export type CaseLinkSlot = (typeof CASE_LINK_SLOTS)[number];
export const CASE_FILE_SLOTS = ["context", "body", "result"] as const;
export type CaseFileSlot = (typeof CASE_FILE_SLOTS)[number];
export const CASE_PLAN_SCOPES = ["day", "week"] as const;
export type CasePlanScope = (typeof CASE_PLAN_SCOPES)[number];

export const CASE_TITLE_MAX = 200;
export const CASE_TEXT_MAX = 10000;
export const CASE_APPEND_NOTE_MAX = 2000;
export const CASE_LINK_TARGET_MAX = 200;
export const CASE_LINK_URL_MAX = 2000;
export const CASE_LINK_LABEL_MAX = 200;
export const CASE_STEP_PARENTS_MAX = 20;
export const CASE_FILES_PER_SLOT = 10;

// ---- answers ------------------------------------------------------------------

/** For day, that date; for week, the Monday of that week (YYYY-MM-DD). */
export interface CasePlan {
  scope: CasePlanScope;
  on: string;
}

/** A thing as the owner sees it now (a wait target, a related row). */
export interface CaseTarget {
  kind: string;
  id: string;
  /** False when it is gone, hidden or of a kind this app does not have. */
  visible: boolean;
  /** The name to show (as written when not visible). */
  label: string;
  /** Whether this app has the kind at all (a screen shows "cannot open here"). */
  known: boolean;
  /** The target's state, for wait kinds that have one (case, action, cloud's issue). */
  state?: CaseStatus;
  /** What a screen needs to build the URL beyond the id. */
  path?: Record<string, unknown>;
  /** Where it lives is out of reach now (a Hub's cloud): not visible, shown as such. */
  unreachable?: boolean;
}

export interface CaseLink {
  id: string;
  kind: string;
  targetId: string;
  label: string | null;
  target: CaseTarget;
}

/** A file bound to a Case (open it at `/api/files/<id>`). */
export interface CaseFile {
  id: string;
  name: string;
  mime: string;
  size: number;
}

export interface CaseStep {
  id: string;
  kind: CaseStepKind;
  title: string;
  body: string;
  result: string | null;
  status: CaseStatus;
  parentIds: string[];
  current: boolean;
  unplaced: boolean;
  waitKind: string | null;
  waitId: string | null;
  waitTarget: CaseTarget | null;
  /** The waiting's kind makes it the owner's own turn (a check of their own). */
  waitMine: boolean;
  waitUntil: string | null;
  dueAt: string | null;
  plan: CasePlan | null;
  someday: boolean;
  links: { body: CaseLink[]; result: CaseLink[] };
  files: { body: CaseFile[]; result: CaseFile[] };
  source: "agent" | "manual";
  createdAt: string;
  closedAt: string | null;
  autoClosedAt: string | null;
  waitTargetDroppedAt: string | null;
  /** What extensions add (an agent chat's part adds the chat the step was delegated to). */
  ext?: Record<string, unknown>;
}

export type CaseRelatedFrom =
  | { from: "manual"; linkId: string }
  | { from: "context" }
  | { from: "step"; stepId: string; slot: CaseLinkSlot | "wait" };

export interface CaseRelated {
  kind: string;
  targetId: string;
  target: CaseTarget;
  /** Where it came from (one row per target; the first source wins, manual first). */
  sources: CaseRelatedFrom[];
}

export interface CaseLabelRef {
  id: string;
  name: string;
}

/** A Case in a list: the open steps only. */
export interface CaseSummary {
  id: string;
  title: string;
  status: CaseStatus;
  ball: CaseBall;
  label: CaseLabelRef | null;
  /** The workspace it belongs to (cloud's), if any. */
  workspaceId: string | null;
  /** Its labels (ids; read the label list once for names and colours), in the owner's order. */
  labelIds: string[];
  openSteps: CaseStep[];
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  ext?: Record<string, unknown>;
}

export interface CaseDetail extends CaseSummary {
  note: string;
  noteRevision: number;
  source: "agent" | "manual";
  steps: CaseStep[];
  related: CaseRelated[];
  files: CaseFile[];
  agentMemo: { body: string; version: number; logCount: number };
}

export interface CaseList {
  items: CaseSummary[];
  nextCursor: string | null;
}

/** An Action as a day view lists it (TimeBlocker), with its Case's title and label. */
export interface CaseDayAction {
  caseId: string;
  caseTitle: string;
  label: CaseLabelRef | null;
  labelIds: string[];
  id: string;
  title: string;
  status: CaseStatus;
  current: boolean;
  plan: CasePlan | null;
  someday: boolean;
  dueAt: string | null;
  closedAt: string | null;
  createdAt: string;
}

// ---- the chain ------------------------------------------------------------------

export interface StepNode {
  id: string;
  kind: CaseStepKind;
  status: CaseStatus;
  parentIds: readonly string[];
  /** ISO 8601; creation order. */
  createdAt: string;
}

const isOpen = (s: { status: CaseStatus }) => s.status === "open";

function byCreated(
  a: { id: string; createdAt: string },
  b: { id: string; createdAt: string },
): number {
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function currentStepIds(steps: readonly StepNode[]): Set<string> {
  const openIds = new Set(steps.filter(isOpen).map((s) => s.id));
  const out = new Set<string>();
  for (const s of steps)
    if (isOpen(s) && s.parentIds.every((p) => !openIds.has(p))) out.add(s.id);
  return out;
}

/**
 * Parents first; among the ready ones, the earliest created. Parents not in
 * the list are ignored, and leftovers of a cycle come last in creation order.
 */
export function orderSteps<T extends StepNode>(steps: readonly T[]): T[] {
  const ids = new Set(steps.map((s) => s.id));
  const left = new Map<string, Set<string>>();
  for (const s of steps)
    left.set(
      s.id,
      new Set(s.parentIds.filter((p) => p !== s.id && ids.has(p))),
    );
  const pending = [...steps].sort(byCreated);
  const out: T[] = [];
  for (;;) {
    const at = pending.findIndex((s) => left.get(s.id)?.size === 0);
    if (at < 0) break;
    const [next] = pending.splice(at, 1);
    out.push(next);
    for (const s of pending) left.get(s.id)?.delete(next.id);
  }
  return [...out, ...pending];
}

/** Whether giving `stepId` these parents makes a cycle (itself included). */
export function wouldCycle(
  steps: readonly Pick<StepNode, "id" | "parentIds">[],
  stepId: string,
  parentIds: readonly string[],
): boolean {
  const parentsOf = new Map(steps.map((s) => [s.id, s.parentIds]));
  const seen = new Set<string>();
  const stack = [...parentIds];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (id === stepId) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const p of parentsOf.get(id) ?? []) stack.push(p);
  }
  return false;
}

export function defaultParentIds(
  steps: readonly Pick<StepNode, "id" | "parentIds">[],
  addedInCall: readonly string[],
): string[] | null {
  if (addedInCall.length > 0) return [addedInCall[addedInCall.length - 1]];
  if (steps.length === 0) return [];
  const hasChild = new Set(steps.flatMap((s) => s.parentIds));
  const leaves = steps.filter((s) => !hasChild.has(s.id));
  return leaves.length === 1 ? [leaves[0].id] : null;
}

/** The step closed last (newest closedAt, then newest createdAt): where the screen adds. */
export function lastClosedStepId(
  steps: readonly { id: string; createdAt: string; closedAt: string | null }[],
): string | null {
  let last: (typeof steps)[number] | null = null;
  for (const s of steps) {
    if (!s.closedAt) continue;
    if (
      !last?.closedAt ||
      Date.parse(s.closedAt) > Date.parse(last.closedAt) ||
      (Date.parse(s.closedAt) === Date.parse(last.closedAt) &&
        Date.parse(s.createdAt) > Date.parse(last.createdAt))
    )
      last = s;
  }
  return last?.id ?? null;
}

export function ballOfSteps(
  steps: readonly (StepNode & {
    waitUntil: string | null;
    waitTargetDroppedAt?: string | null;
    /** The waiting's kind makes it the owner's own turn. */
    waitMine?: boolean;
  })[],
  now: number,
): CaseBall {
  const current = currentStepIds(steps);
  const cur = steps.filter((s) => current.has(s.id));
  if (cur.some((s) => s.kind === "action" || s.waitMine === true)) return "me";
  const waits = cur.filter((s) => s.kind === "waiting");
  if (waits.length === 0) return "none";
  return waits.some(
    (s) =>
      (s.waitTargetDroppedAt ?? null) !== null ||
      (s.waitUntil !== null && Date.parse(s.waitUntil) <= now),
  )
    ? "due"
    : "them";
}

/**
 * Wait cycles across Cases: `from → to` means Case `from` waits on Case `to`
 * (or an Action in it). The shortest path from `from` to `to`, both ends
 * included, or null.
 */
export function waitCyclePath(
  edges: readonly { from: string; to: string }[],
  from: string,
  to: string,
): string[] | null {
  const next = new Map<string, string[]>();
  for (const e of edges) {
    const list = next.get(e.from);
    if (list) list.push(e.to);
    else next.set(e.from, [e.to]);
  }
  const via = new Map<string, string | null>([[from, null]]);
  const queue = [from];
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i];
    if (id === to) {
      const path: string[] = [];
      for (let at: string | null = id; at !== null; at = via.get(at) ?? null)
        path.unshift(at);
      return path;
    }
    for (const n of next.get(id) ?? []) {
      if (via.has(n)) continue;
      via.set(n, id);
      queue.push(n);
    }
  }
  return null;
}

// ---- plans --------------------------------------------------------------------

/**
 * Where an Action belongs by its plan (null for closed steps and waitings):
 * inbox (no plan, not someday), someday, planned (today or later), carried
 * (the plan's last day is before today and it is still open).
 */
export type CasePlanState = "inbox" | "someday" | "planned" | "carried";

function utcDate(date: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

const toDateOnly = (t: Date) => t.toISOString().slice(0, 10);

export function addDays(date: string, days: number): string {
  const t = utcDate(date);
  t.setUTCDate(t.getUTCDate() + days);
  return toDateOnly(t);
}

/** b − a in days. */
export function diffDays(a: string, b: string): number {
  return Math.round((utcDate(b).getTime() - utcDate(a).getTime()) / 86_400_000);
}

/** Whether `date` is a real YYYY-MM-DD date. */
export function isDateOnly(date: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const [y, m, d] = date.split("-").map(Number);
  const t = utcDate(date);
  return (
    t.getUTCFullYear() === y &&
    t.getUTCMonth() === m - 1 &&
    t.getUTCDate() === d
  );
}

export function weekStartOf(date: string): string {
  const dow = utcDate(date).getUTCDay();
  return addDays(date, -((dow + 6) % 7));
}

export function planLastDay(plan: CasePlan): string {
  return plan.scope === "day" ? plan.on : addDays(plan.on, 6);
}

export function casePlanState(
  step: {
    kind: string;
    status: string;
    plan: CasePlan | null;
    someday: boolean;
  },
  today: string,
): CasePlanState | null {
  if (step.kind !== "action" || step.status !== "open") return null;
  if (!step.plan) return step.someday ? "someday" : "inbox";
  return diffDays(planLastDay(step.plan), today) > 0 ? "carried" : "planned";
}

/** Plan input (`{ scope, on }`, "someday" or null) → the step's fields (week moved to Monday). */
export function planFields(input: CasePlan | "someday" | null): {
  plan: CasePlan | null;
  someday: boolean;
} {
  if (input === null) return { plan: null, someday: false };
  if (input === "someday") return { plan: null, someday: true };
  return {
    plan: {
      scope: input.scope,
      on: input.scope === "week" ? weekStartOf(input.on) : input.on,
    },
    someday: false,
  };
}

export function planCovers(plan: CasePlan, day: string): boolean {
  return plan.scope === "day"
    ? plan.on === day
    : plan.on <= day && day <= planLastDay(plan);
}
